import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import { NavLink as Link } from 'react-router-dom';
import {
  NavLink, NavHeader, FeatureGate, ProductLogo,
} from '../components';
import { ReactComponent as IconAssist } from '../assets/icon-agent-assist.svg';
import { ReactComponent as IconContext } from '../assets/icon-conversation-context-service.svg';
import { ReactComponent as IconRouting } from '../assets/icon-dynamic-routing.svg';
import { ReactComponent as IconEngagementController } from '../assets/icon-engagement-controller.svg';
import { useAppState } from '../hooks';
import { ReactComponent as IconCode } from '../assets/icon-developers.svg';

const backgroundColor = theme('mode', {
  light: (props) => props.theme.colors.white,
  dark: (props) => props.theme.colors.lpGhost,
});

const iconColor = theme('mode', {
  light: (props) => props.theme.colors.mediumPurple,
  dark: (props) => props.theme.colors.white,
});

const borderColor = theme('mode', {
  light: (props) => props.theme.colors.lightBlue2,
  dark: (props) => props.theme.lpColorsDark.gray59,
});

const Assist = styled(IconAssist)`color: ${iconColor};`;
const Context = styled(IconContext)`color: ${iconColor};`;
const Routing = styled(IconRouting)`color: ${iconColor};`;
const Code = styled(IconCode)`color: ${iconColor};`;

const NavWrapper = styled(Flex)`
  background-color: ${backgroundColor};
  border-radius: ${(props) => props.theme.radius.large} ${(props) => props.theme.radius.large} 0 0;
`;

const NavSection = styled(Flex)`
  border-color: ${borderColor};
  border-width: 1px 0 0 0;
  border-style: solid;
  flex-shrink: 0;
`;

const NavItems = styled(Flex)``;

function Nav(props) {
  const { path } = props;
  const { features, isLoading } = useAppState();

  if (isLoading) {
    return null;
  }

  const isAgentAssistEnabled = () => features?.['maven.agentassist.enabled'];
  const hasRecommendedActionsFeature = () => features?.['maven.feature.recommendedActionBots']
    || features?.['maven.feature.recommendedActionKBs'];

  return (
    <NavWrapper flexDirection="column" data-testid="nav" py={3} px={3} mt={3} ml={3}>
      <Link to="/" style={{ textDecoration: 'none' }}>
        <Box pt={2} ml={3}>
          <ProductLogo
            maxWidth={150}
            fontSize={2}
            fontWeight={100}
            logoWidth="28px"
            logoHeight="33px"
            ml={2}
          />
        </Box>
      </Link>
      <NavItems flexDirection="column">
        {isAgentAssistEnabled() && hasRecommendedActionsFeature() && (
          <NavSection flexDirection="column" py={3} pl={3}>
            <FeatureGate feature="maven.feature.recommendedActionBots">
              <NavHeader
                to={features['maven.feature.recommendedActionBots'] ? '/recommended-actions' : '/knowledge-base'}
                label="Agent Assist"
                Icon={Assist}
              />
            </FeatureGate>
            <FeatureGate feature="maven.feature.recommendedActionBots">
              <NavLink to="/recommended-actions" label="Bots" path={path} />
            </FeatureGate>
            <FeatureGate feature="maven.feature.recommendedActionKBs">
              <NavLink to="/knowledge-base" label="Knowledge Bases" path={path} />
            </FeatureGate>
          </NavSection>
        )}
        <FeatureGate feature="maven.feature.contextVariables">
          <NavSection flexDirection="column" py={3} pl={3}>
            <NavHeader to="/conversation-context" label="Conversation Context Service" Icon={Context} />
            <NavLink to="/conversation-context" label="Conversation" path={path} />
            <NavLink to="/custom-context" label="Custom" path={path} />
            <NavLink to="/intents" label="Intents" path={path} />
            <FeatureGate feature="maven.feature.sdePage">
              <NavLink to="/sde-context" label="Structured Data Entities (SDE)" path={path} />
            </FeatureGate>
          </NavSection>
        </FeatureGate>
        <FeatureGate feature="maven.feature.aiPoweredRouting">
          <NavSection flexDirection="column" py={3} pl={3} data-testid="nav-section-ai-routing">
            <NavHeader
              to="/intent-content-policies"
              label="Dynamic Routing"
              Icon={Routing}
            />
            <NavLink
              data-testid="nav-link-manage-policies"
              to="/intent-content-policies"
              label="Manage Policies"
              path={path}
            />
            <FeatureGate feature="isCcsV2">
              <NavLink
                data-testid="nav-link-policy-logs"
                to="/policy-logs"
                label="Policy Logs"
                path={path}
              />
            </FeatureGate>
          </NavSection>
        </FeatureGate>
        <FeatureGate
          feature="maven.feature.askMaven"
        >
          <NavSection flexDirection="column" py={3} pl={3}>
            <NavHeader to="/ask-maven" label="Developers" Icon={Code} />
            <FeatureGate feature="isCcsV2" negate>
              <NavLink to="/ask-maven" label="APIs" path={path} />
            </FeatureGate>
            <NavLink to="/settings" label="API Authorization" path={path} />
          </NavSection>
        </FeatureGate>
        <NavSection flexDirection="column" py={3} pl={3}>
          <NavHeader to="/engagement-controller" label="Engagement Controller" Icon={IconEngagementController} />
          <NavLink to="/engagement-controller" label="Manage Rules" path={path} />
        </NavSection>
      </NavItems>
    </NavWrapper>
  );
}

Nav.defaultProps = {
  path: undefined,
};

Nav.propTypes = {
  path: PropTypes.string,
};

export default Nav;
